import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cpu, Radio, ShieldAlert, TerminalSquare, X } from 'lucide-react';
import api from '../services/api';
import {
  HANGAR_MODEL_CATALOG,
  getElectronicWarfareCapabilities,
  getElectronicWarfareProfile
} from '../data/accessories';

const BOOT_SEQUENCE = [
  { delay: 180, type: 'system', text: 'LINKING EW BUS...' },
  { delay: 520, type: 'system', text: 'READING BOT TELEMETRY...' },
  { delay: 940, type: 'system', text: 'CHECKING MODULE SIGNATURE...' },
  { delay: 1320, type: 'success', text: 'SYSTEM DATA LOADED' },
  { delay: 1640, type: 'system', text: 'TYPE help TO SEE AVAILABLE COMMANDS' }
];

function normalizeCommand(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function formatCap(value) {
  return value ? 'ON' : 'OFF';
}

export default function ElectronicWarfareTerminalModal({
  open,
  onClose,
  botName,
  moduleRow,
  cityHackTarget = null,
  onCityBypassAuthorized
}) {
  const logsBottomRef = useRef(null);
  const inputRef = useRef(null);
  const [input, setInput] = useState('');
  const [logs, setLogs] = useState([]);
  const [booting, setBooting] = useState(true);

  const profile = useMemo(() => getElectronicWarfareProfile(moduleRow), [moduleRow]);
  const capabilities = useMemo(() => getElectronicWarfareCapabilities(moduleRow), [moduleRow]);
  const availableCommands = useMemo(() => {
    const commands = [
      'help',
      'status',
      'profile',
      'modules',
      'scan',
      'clear',
      'exit'
    ];

    if (capabilities.canBypassForeignCities) commands.push('city bypass');
    if (capabilities.canJamMines) commands.push('jam mines');
    if (capabilities.canDisruptEnemies) commands.push('jam enemies');
    return commands;
  }, [capabilities.canBypassForeignCities, capabilities.canDisruptEnemies, capabilities.canJamMines]);

  const appendLog = useCallback((type, text) => {
    setLogs((current) => [
      ...current,
      {
        id: `${Date.now()}-${Math.random()}`,
        type,
        text
      }
    ]);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    setInput('');
    setLogs([]);
    setBooting(true);

    const timers = BOOT_SEQUENCE.map((entry) => (
      setTimeout(() => {
        appendLog(entry.type, entry.text);
      }, entry.delay)
    ));
    const readyTimer = setTimeout(() => {
      setBooting(false);
      appendLog('system', 'EW CONSOLE READY');
    }, BOOT_SEQUENCE[BOOT_SEQUENCE.length - 1].delay + 180);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      clearTimeout(readyTimer);
    };
  }, [appendLog, moduleRow, open]);

  useEffect(() => {
    if (!open || booting) return;
    inputRef.current?.focus();
  }, [booting, open]);

  useEffect(() => {
    logsBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logs, booting, open]);

  function pushSystemBlock(lines, type = 'system') {
    appendLog(type, Array.isArray(lines) ? lines.join('\n') : String(lines || ''));
  }

  function handleCommand(event) {
    event.preventDefault();
    const command = normalizeCommand(input);
    if (!command) return;

    const lowered = command.toLowerCase();
    appendLog('user', `ew> ${command}`);
    setInput('');

    if (lowered === 'help' || lowered === '/help') {
      pushSystemBlock([
        'AVAILABLE COMMANDS:',
        ...availableCommands.map((command) => `- ${command}`),
        '',
        'HINTS:',
        'status, profile, modules and scan adapt to the installed module'
      ]);
      return;
    }

    if (lowered === 'status' || lowered === '/status') {
      pushSystemBlock([
        `BOT: ${botName || 'UNKNOWN'}`,
        `MODULE: ${profile.name}`,
        `POWER: ${profile.power}`,
        `LEVEL REQ: ${profile.level}`,
        `CITIES: ${formatCap(capabilities.canBypassForeignCities)}`,
        `MINES: ${formatCap(capabilities.canJamMines)}`,
        `ENEMY JAM: ${formatCap(capabilities.canDisruptEnemies)}`
      ]);
      return;
    }

    if (lowered === 'profile' || lowered === '/profile') {
      pushSystemBlock([
        `MODEL: ${profile.name}`,
        `QUALITY: ${profile.quality}`,
        `POWER: ${profile.power}`,
        `DESCRIPTION: ${profile.description}`,
        `FOCUS: ${profile.qualities.length ? profile.qualities.join(' | ') : 'NO EXTRA NOTES'}`
      ]);
      return;
    }

    if (lowered === 'modules' || lowered === '/modules') {
      pushSystemBlock([
        'EW CATALOG:',
        ...HANGAR_MODEL_CATALOG.ewar.map((module) => {
          const unlocked = Number(module.level || 0) <= Number(profile.level || 0);
          const active = module.name === profile.name;
          return `- ${module.name} [L${module.level}] P${module.power} ${active ? '(ACTIVE)' : unlocked ? '(READY)' : '(LOCKED)'}`;
        })
      ]);
      return;
    }

    if (lowered === 'scan' || lowered === '/scan') {
      pushSystemBlock([
        `SIGNATURE LOCK: ${profile.quality}`,
        `CITY BYPASS: ${formatCap(capabilities.canBypassForeignCities)}`,
        `MINE JAM: ${formatCap(capabilities.canJamMines)}`,
        `ENEMY JAM: ${formatCap(capabilities.canDisruptEnemies)}`,
        capabilities.canJamMines
          ? 'MINEFIELD SPOOF: AVAILABLE'
          : 'MINEFIELD SPOOF: INSUFFICIENT MODULE LEVEL'
      ]);
      return;
    }

    if (lowered === 'city bypass' || lowered === 'bypass city' || lowered === '/city bypass') {
      if (!capabilities.canBypassForeignCities) {
        appendLog('error', 'ACCESS DENIED');
        appendLog('warning', 'MODULE DOES NOT SUPPORT FOREIGN CITY BYPASS');
        return;
      }
      const targetSectorId = Number(cityHackTarget?.sectorId || 0);
      const targetCoordinate = Number(cityHackTarget?.coordinate);
      if (!Number.isInteger(targetSectorId) || targetSectorId <= 0 || !Number.isFinite(targetCoordinate)) {
        appendLog('warning', 'NO CITY ENTRY TARGET ACTIVE');
        appendLog('system', 'OPEN THE CITY ENTRY MODAL FIRST');
        return;
      }

      appendLog('system', `TARGET LOCKED: ${cityHackTarget.city?.name || 'UNKNOWN CITY'}`);
      appendLog('system', 'REQUESTING TERMINAL AUTHORIZATION...');
      api.post('/robots/authorize-city-bypass', {
        sectorId: targetSectorId,
        coordinate: targetCoordinate
      })
        .then((response) => {
          appendLog('success', response.data?.message || 'FOREIGN CITY ROUTE AUTHORIZED');
          appendLog('system', 'GATES OPENED FOR STEALTH ACCESS');
          onCityBypassAuthorized?.(response.data);
        })
        .catch((err) => {
          appendLog('error', 'AUTHORIZATION FAILED');
          appendLog('warning', err.response?.data?.error || 'CITY ENTRY BYPASS REJECTED');
        });
      return;
    }

    if (lowered === 'jam mines' || lowered === '/jam mines') {
      if (!capabilities.canJamMines) {
        appendLog('error', 'ACCESS DENIED');
        appendLog('warning', 'MINE JAM NOT AVAILABLE IN THIS MODEL');
        return;
      }
      appendLog('success', 'MINE JAM ACTIVE');
      appendLog('system', 'DETENTION FIELD BLENDED WITH TERRAIN');
      return;
    }

    if (lowered === 'jam enemies' || lowered === '/jam enemies') {
      if (!capabilities.canDisruptEnemies) {
        appendLog('error', 'ACCESS DENIED');
        appendLog('warning', 'ENEMY JAM NOT AVAILABLE IN THIS MODEL');
        return;
      }
      appendLog('success', 'ENEMY SENSOR SPOOF ACTIVE');
      appendLog('system', 'SIGNAL MASKING ENGAGED');
      return;
    }

    if (lowered === 'clear' || lowered === '/clear') {
      setLogs([]);
      return;
    }

    if (lowered === 'exit' || lowered === '/exit' || lowered === 'close') {
      onClose?.();
      return;
    }

    appendLog('error', 'COMMAND NOT RECOGNIZED');
    appendLog('warning', 'TYPE help');
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-3 py-4">
      <div className="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-emerald-400/20 bg-[#020608] shadow-[0_0_60px_rgba(34,197,94,0.15)]">
        <div className="relative overflow-hidden border-b border-emerald-400/15 bg-[linear-gradient(180deg,rgba(10,24,20,0.98),rgba(3,8,8,0.96))] px-5 py-4">
          <div className="absolute inset-0 ew-terminal-scanlines opacity-35" />
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-400/10 text-emerald-200">
                <TerminalSquare size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.35em] text-emerald-200/60">EW TERMINAL</div>
                <div className="mt-1 text-lg font-bold text-emerald-100">{profile.name}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.24em] text-emerald-200/70">
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1">BOT {botName || 'UNKNOWN'}</span>
              <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1">POWER {profile.power}</span>
              <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1">L{profile.level}</span>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-full border border-slate-600/80 px-3 py-1 text-slate-200 transition hover:border-rose-300/50 hover:text-rose-200"
              >
                <X size={14} />
                Close
              </button>
            </div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="overflow-y-auto border-b border-emerald-400/15 bg-[#04100d] p-4 lg:border-b-0 lg:border-r">
            <div className="rounded-2xl border border-emerald-400/15 bg-black/30 p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-emerald-200/60">
                <Cpu size={14} />
                System loadout
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-200">
                <div className="rounded-xl border border-slate-700/80 bg-slate-950/50 p-3">
                  <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-[0.2em]">
                    <ShieldAlert size={12} />
                    Module status
                  </div>
                  <div className="mt-1 font-semibold text-emerald-100">{capabilities.hasModule ? 'Linked' : 'Offline'}</div>
                  <div className="mt-1 text-xs text-slate-400">Strength {capabilities.strength}</div>
                </div>
                <div className="rounded-xl border border-slate-700/80 bg-slate-950/50 p-3">
                  <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Capabilities</div>
                  <div className="mt-2 space-y-1 text-xs text-slate-300">
                    <div>City bypass: {formatCap(capabilities.canBypassForeignCities)}</div>
                    <div>Mine jam: {formatCap(capabilities.canJamMines)}</div>
                    <div>Enemy jam: {formatCap(capabilities.canDisruptEnemies)}</div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-700/80 bg-slate-950/50 p-3">
                  <div className="text-slate-400 text-xs uppercase tracking-[0.2em]">Model notes</div>
                  <div className="mt-2 text-xs leading-6 text-slate-300">
                    {profile.qualities.length ? profile.qualities.join(' / ') : 'No extra notes'}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-cyan-500/15 bg-cyan-500/5 p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-cyan-200/70">
                <Radio size={14} />
                Quick hints
              </div>
              <ul className="mt-3 space-y-2 text-sm text-cyan-50/85">
                <li>help reveals the active command set.</li>
                <li>profile shows the installed EW model.</li>
                <li>modules lists the whole catalog and lock state.</li>
                <li>scan reports what this module can do right now.</li>
                {cityHackTarget && (
                  <li>
                    target lock: {cityHackTarget.city?.name || 'unknown city'} at {cityHackTarget.coordinate}
                  </li>
                )}
              </ul>
            </div>
          </aside>

          <section className="flex min-h-0 flex-col bg-[#020608] lg:min-h-[540px]">
            <div className={`border-b border-emerald-400/10 px-4 py-3 text-xs uppercase tracking-[0.3em] ${booting ? 'text-amber-200/80' : 'text-emerald-200/70'}`}>
              {booting ? 'Loading system data...' : 'Console ready'}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 font-mono text-[14px] leading-7 text-emerald-100">
              {logs.map((entry) => {
                const palette = {
                  system: 'text-emerald-100/90',
                  user: 'text-cyan-200',
                  success: 'text-lime-300',
                  warning: 'text-amber-300',
                  error: 'text-rose-300'
                };

                return (
                  <div key={entry.id} className={`whitespace-pre-line ${palette[entry.type] || palette.system}`}>
                    {entry.text}
                  </div>
                );
              })}

              {booting && (
                <div className="mt-4 space-y-3">
                  <div className="h-2 w-full overflow-hidden rounded-full border border-emerald-400/20 bg-slate-950">
                    <div className="ew-terminal-loader h-full w-1/2 rounded-full" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.25em] text-emerald-200/55">
                    Syncing sensor data from bot kernel
                  </div>
                </div>
              )}

              {!booting && (
                <form onSubmit={handleCommand} className="mt-6 flex flex-wrap items-center gap-3">
                  <span className="text-emerald-300">ew&gt;</span>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    className="crt-input ew-terminal-input flex-1 min-w-[220px] border-b border-emerald-400/20 bg-transparent px-0 py-2 text-emerald-100 focus:outline-none focus:border-emerald-300"
                    placeholder="help"
                    autoComplete="off"
                    spellCheck="false"
                  />
                  <button
                    type="submit"
                    className="rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-emerald-100 transition hover:bg-emerald-400/20"
                  >
                    Run
                  </button>
                </form>
              )}

              <div ref={logsBottomRef} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
